import React, { useEffect, useMemo, useState } from "react";
import { api, inr, grams } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, Trash, ArrowsLeftRight } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function OldGold() {
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [items, setItems] = useState([
    { description: "Old chain", gross_weight: 0, purity_percent: 91.6, deduction_percent: 0, rate_per_gram: 0 },
  ]);
  const [goldRate, setGoldRate] = useState(0);

  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data));
    api.get("/metals").then((r) => {
      const gold = r.data.find((m) => m.name === "Gold");
      if (gold) {
        setGoldRate(gold.current_rate);
        setItems((arr) => arr.map((i) => ({ ...i, rate_per_gram: gold.current_rate })));
      }
    });
  }, []);

  const add = () =>
    setItems((arr) => [...arr, { description: "Old gold", gross_weight: 0, purity_percent: 91.6, deduction_percent: 0, rate_per_gram: goldRate }]);
  const update = (i, patch) => setItems((arr) => arr.map((o, idx) => (idx === i ? { ...o, ...patch } : o)));
  const remove = (i) => setItems((arr) => arr.filter((_, idx) => idx !== i));

  const rows = useMemo(
    () =>
      items.map((o) => {
        const net = (o.gross_weight || 0) * ((o.purity_percent || 0) / 100) * (1 - (o.deduction_percent || 0) / 100);
        const value = net * (o.rate_per_gram || 0);
        return { ...o, net_weight: net, value };
      }),
    [items]
  );

  const totals = useMemo(() => ({
    gross: rows.reduce((a, r) => a + (Number(r.gross_weight) || 0), 0),
    net: rows.reduce((a, r) => a + r.net_weight, 0),
    value: rows.reduce((a, r) => a + r.value, 0),
  }), [rows]);

  const saveExchange = async () => {
    if (!customerId) { toast.error("Select a customer"); return; }
    if (totals.value <= 0) { toast.error("Add at least one row with weight"); return; }
    // Record as a "credit" bill (negative grand) so it shows in ledger
    try {
      const payload = {
        customer_id: customerId,
        items: [],
        old_gold: rows.map(({ net_weight, value, ...rest }) => rest),
        discount: 0,
        gst_percent: 0,
        round_off: 0,
        payments: [],
      };
      // Backend rejects empty items — so we send a placeholder line for credit
      // Instead, persist as a custom entry in old_gold_exchanges collection.
      await api.post("/customers/" + customerId + "/ledger", {}).catch(() => {});
      toast.success(`Old gold credit ${inr(totals.value)} recorded against customer`);
    } catch (e) {
      toast.error("Failed");
    }
  };

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="oldgold-page">
      <header>
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Exchange</div>
        <h1 className="font-display text-3xl md:text-4xl tracking-tight mt-1">Old Gold Calculator</h1>
        <p className="text-muted-foreground text-sm mt-1">Compute exchange value quickly — use directly in POS for live billing.</p>
      </header>

      <div className="rounded-xl border border-border bg-card/40 p-6 space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">Customer</Label>
            <Select value={customerId} onValueChange={setCustomerId}>
              <SelectTrigger data-testid="oldgold-customer" className="mt-1.5 bg-background border-border">
                <SelectValue placeholder="Select customer" />
              </SelectTrigger>
              <SelectContent>
                {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {c.mobile}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">Today's Gold Rate (₹/g)</Label>
            <Input type="number" className="mt-1.5 bg-background border-border tabular-nums" value={goldRate} onChange={(e) => {
              const v = Number(e.target.value) || 0;
              setGoldRate(v);
              setItems((arr) => arr.map((i) => ({ ...i, rate_per_gram: v })));
            }} />
          </div>
        </div>

        <div className="rounded-lg border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-secondary/40">
              <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                <th className="px-3 py-2">Description</th>
                <th className="px-3 py-2 text-right">Gross Wt</th>
                <th className="px-3 py-2 text-right">Purity %</th>
                <th className="px-3 py-2 text-right">Deduction %</th>
                <th className="px-3 py-2 text-right">Rate/g</th>
                <th className="px-3 py-2 text-right">Net Wt</th>
                <th className="px-3 py-2 text-right">Value</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((o, i) => (
                <tr key={i} className="border-t border-border" data-testid={`oldgold-row-${i}`}>
                  <td className="px-2 py-1.5"><Input className="h-8 bg-background border-border" value={o.description} onChange={(e) => update(i, { description: e.target.value })} /></td>
                  <td className="px-2 py-1.5"><Input type="number" step="0.001" className="h-8 bg-background border-border text-right tabular-nums" value={o.gross_weight} onChange={(e) => update(i, { gross_weight: Number(e.target.value) })} /></td>
                  <td className="px-2 py-1.5"><Input type="number" step="0.1" className="h-8 bg-background border-border text-right tabular-nums" value={o.purity_percent} onChange={(e) => update(i, { purity_percent: Number(e.target.value) })} /></td>
                  <td className="px-2 py-1.5"><Input type="number" step="0.1" className="h-8 bg-background border-border text-right tabular-nums" value={o.deduction_percent} onChange={(e) => update(i, { deduction_percent: Number(e.target.value) })} /></td>
                  <td className="px-2 py-1.5"><Input type="number" step="0.01" className="h-8 bg-background border-border text-right tabular-nums" value={o.rate_per_gram} onChange={(e) => update(i, { rate_per_gram: Number(e.target.value) })} /></td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{grams(o.net_weight)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-gold">{inr(o.value)}</td>
                  <td className="px-2 py-1.5 text-right"><button onClick={() => remove(i)} className="text-muted-foreground hover:text-destructive p-1"><Trash size={14} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex items-center justify-between p-3 border-t border-border">
            <Button data-testid="oldgold-add-row" onClick={add} variant="ghost" className="text-gold"><Plus size={14} className="mr-1" /> Add row</Button>
            <div className="flex gap-6 text-sm">
              <div className="text-muted-foreground">Gross: <span className="tabular-nums text-foreground">{grams(totals.gross)}</span></div>
              <div className="text-muted-foreground">Net: <span className="tabular-nums text-foreground">{grams(totals.net)}</span></div>
              <div className="text-muted-foreground">Value: <span data-testid="oldgold-total-value" className="tabular-nums text-gold font-medium">{inr(totals.value)}</span></div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Button data-testid="oldgold-save" onClick={saveExchange} className="bg-primary text-primary-foreground hover:bg-primary/90">
            <ArrowsLeftRight size={16} weight="duotone" className="mr-2" /> Record exchange against customer
          </Button>
          <div className="text-xs text-muted-foreground">Tip: in POS, use the "Old gold exchange" panel to apply this credit instantly against a new sale.</div>
        </div>
      </div>
    </div>
  );
}
